import { t } from "@/i18n/translate";

/**
 * Stops motion coming from animated media (GIFs and autoplaying videos).
 *
 * CSS cannot pause an animated GIF, so the Stop Animations tool needs this
 * JS pass in addition to the CSS it injects.
 *
 * GIFs:
 *   - Same-origin → frozen on their current frame via <canvas> (seamless,
 *     keeps the real picture).
 *   - Cross-origin → the canvas is tainted and cannot be exported, so we fall
 *     back to a clean static "Animation paused" placeholder that preserves the
 *     layout box. (A frozen real frame would require a server-side proxy.)
 *
 * Videos: paused and stripped of autoplay/loop while enabled.
 *
 * The original source/state is stashed on the element so toggling the tool off
 * restores everything exactly.
 */

const GIF_RE = /\.gif(\?|#|$)/i;

function isGif(img: HTMLImageElement): boolean {
    return GIF_RE.test(img.currentSrc || img.src || "");
}

interface PlaceholderTheme {
    /** Solid colour the tint/icon/text are drawn in (opposite of the page bg). */
    base: string;
    tintOpacity: number;
    iconOpacity: number;
    textOpacity: number;
}

/**
 * Walk up from the image to the first ancestor with a non-transparent
 * background colour, so the placeholder can blend with the surrounding section.
 * Falls back to white (typical page background).
 */
function effectiveBgRgb(el: HTMLElement): [number, number, number] {
    let node: HTMLElement | null = el.parentElement;
    while (node) {
        const m = getComputedStyle(node).backgroundColor.match(
            /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
        );
        if (m && (m[4] === undefined || parseFloat(m[4]) > 0)) {
            return [Number(m[1]), Number(m[2]), Number(m[3])];
        }
        node = node.parentElement;
    }
    return [255, 255, 255];
}

function isLightBg([r, g, b]: [number, number, number]): boolean {
    // Perceived luminance (sRGB approximation).
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
}

/**
 * The placeholder is a translucent tint so the real site background shows
 * through and it blends with the layout. On light pages it is a 20% black
 * wash with dark icon/text; on dark pages a subtle white wash with light
 * icon/text — so it stays visible and legible on any theme.
 */
function placeholderTheme(img: HTMLImageElement): PlaceholderTheme {
    return isLightBg(effectiveBgRgb(img))
        ? { base: "#000000", tintOpacity: 0.2, iconOpacity: 0.55, textOpacity: 0.6 }
        : { base: "#ffffff", tintOpacity: 0.12, iconOpacity: 0.7, textOpacity: 0.75 };
}

function buildPlaceholder(width: number, height: number, label: string, theme: PlaceholderTheme): string {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    const cx = w / 2;
    const cy = h / 2;
    // Two pause bars centred, with the label below. Translucent fills (via
    // fill-opacity) so the page background shows through and it matches the site.
    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
        `<rect width="100%" height="100%" fill="${theme.base}" fill-opacity="${theme.tintOpacity}"/>` +
        `<g fill="${theme.base}" fill-opacity="${theme.iconOpacity}">` +
        `<rect x="${cx - 14}" y="${cy - 28}" width="9" height="34" rx="2"/>` +
        `<rect x="${cx + 5}" y="${cy - 28}" width="9" height="34" rx="2"/>` +
        `</g>` +
        `<text x="50%" y="${cy + 34}" font-family="sans-serif" font-size="14" fill="${theme.base}" fill-opacity="${theme.textOpacity}" text-anchor="middle">${label}</text>` +
        `</svg>`;
    return "data:image/svg+xml," + encodeURIComponent(svg);
}

function freezeGif(img: HTMLImageElement, label: string): void {
    // Already handled.
    if (img.dataset.cya11yOrgSrc !== undefined) return;

    const renderW = img.clientWidth || img.naturalWidth || 0;
    const renderH = img.clientHeight || img.naturalHeight || 0;

    // Only the src is swapped. alt / aria-* / role / title are left untouched so
    // the image keeps its original accessible name for screen-reader users. The
    // visible "Animation paused" caption lives inside the placeholder SVG, which
    // is NOT exposed to assistive tech (text in an <img src> is not announced),
    // so it never overrides the author's alt text.
    img.dataset.cya11yOrgSrc = img.src;

    try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || renderW || 1;
        canvas.height = img.naturalHeight || renderH || 1;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("no 2d context");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // Throws SecurityError for cross-origin (tainted) canvases.
        img.src = canvas.toDataURL("image/png");
    } catch {
        // Cross-origin GIF: cannot read pixels — fall back to a placeholder
        // that blends with the page background behind the image.
        img.src = buildPlaceholder(renderW || 200, renderH || 150, label, placeholderTheme(img));
    }
}

function restoreGif(img: HTMLImageElement): void {
    const orig = img.dataset.cya11yOrgSrc;
    if (orig === undefined) return;

    img.src = orig;
    delete img.dataset.cya11yOrgSrc;
}

function pauseVideo(video: HTMLVideoElement): void {
    if (video.dataset.cya11yVideoPaused !== undefined) return;

    video.dataset.cya11yVideoPaused = "1";
    video.dataset.cya11yWasPlaying = video.paused ? "0" : "1";
    video.dataset.cya11yWasAutoplay = video.autoplay ? "1" : "0";
    video.dataset.cya11yWasLoop = video.loop ? "1" : "0";

    video.autoplay = false;
    video.loop = false;
    video.pause();
}

function restoreVideo(video: HTMLVideoElement): void {
    if (video.dataset.cya11yVideoPaused === undefined) return;

    video.autoplay = video.dataset.cya11yWasAutoplay === "1";
    video.loop = video.dataset.cya11yWasLoop === "1";
    const wasPlaying = video.dataset.cya11yWasPlaying === "1";

    delete video.dataset.cya11yVideoPaused;
    delete video.dataset.cya11yWasPlaying;
    delete video.dataset.cya11yWasAutoplay;
    delete video.dataset.cya11yWasLoop;

    if (wasPlaying) {
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
    }
}

export default function disableAnimatedImages(enable = false): void {
    const label = t("Animation paused");

    document.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
        // Never touch the widget's own UI.
        if (img.closest("#cya11y-container")) return;

        if (enable) {
            if (isGif(img)) freezeGif(img, label);
        } else {
            restoreGif(img);
        }
    });

    document.querySelectorAll<HTMLVideoElement>("video").forEach((video) => {
        if (video.closest("#cya11y-container")) return;
        if (enable) pauseVideo(video);
        else restoreVideo(video);
    });
}
